import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  Receipt, 
  FileText,
  ArrowLeft,
  TrendingUp,
  Wallet,
  BarChart3
} from "lucide-react";
import { generateFiscalPDF } from "../utils/generateFiscalPDF";
import { toast } from "sonner";

import { API } from "../utils/api";

export default function Earnings() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [fiscal, setFiscal] = useState(null);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) fetchFiscalData();
  }, [user, fiscalYear]);

  const fetchFiscalData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stats/fiscal?year=${fiscalYear}`);
      setFiscal(res.data);
    } catch (error) {
      console.error("Error fetching fiscal data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || (loading && !fiscal)) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!user) return null;

  const a = fiscal?.annual || { gross: 0, commission: 0, base: 0, iva: 0, irpf: 0, net: 0, bookings_count: 0 };

  return (
    <div className="pt-20 min-h-screen" data-testid="earnings-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => navigate("/dashboard")} 
            className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
            data-testid="earnings-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Ingresos</h1>
            <p className="text-gray-500 text-sm mt-1">Desglose fiscal para la AEAT</p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setFiscalYear(y => y - 1)} 
              className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
              data-testid="fiscal-prev-year"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-white font-bold text-lg min-w-[52px] text-center" data-testid="fiscal-year">{fiscalYear}</span>
            <button 
              onClick={() => setFiscalYear(y => y + 1)} 
              className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
              data-testid="fiscal-next-year"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                if (fiscal) {
                  const result = generateFiscalPDF(fiscal, user?.name);
                  if (result) toast.success(`Resumen fiscal ${fiscalYear} descargado`);
                }
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-500/10 hover:bg-violet-500/20 text-violet-400 text-sm font-medium transition-colors"
              data-testid="fiscal-download-pdf"
            >
              <FileText className="w-4 h-4" />
              Descargar PDF
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#231f30] rounded-2xl border border-white/5 p-5" data-testid="card-gross">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-gray-400" />
              </div>
              <span className="text-gray-500 text-sm">Ingresos Brutos</span>
            </div>
            <p className="text-2xl font-bold text-white">{a.gross.toFixed(2)} <span className="text-lg text-gray-500">€</span></p>
          </div>
          <div className="bg-[#231f30] rounded-2xl border border-white/5 p-5" data-testid="card-base">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                <Wallet className="w-4 h-4 text-gray-400" />
              </div>
              <span className="text-gray-500 text-sm">Base Imponible</span>
            </div>
            <p className="text-2xl font-bold text-white">{a.base.toFixed(2)} <span className="text-lg text-gray-500">€</span></p>
          </div>
          <div className="bg-[#231f30] rounded-2xl border border-violet-500/20 p-5" data-testid="card-net">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-violet-400" />
              </div>
              <span className="text-gray-500 text-sm">Neto a Percibir</span>
            </div>
            <p className="text-2xl font-bold text-violet-400">{a.net.toFixed(2)} <span className="text-lg text-violet-400/60">€</span></p>
          </div>
        </div>

        {/* Rates badges */}
        <div className="flex gap-3 mb-6 flex-wrap">
          {[
            { label: "Comisión Plazafy", value: `${(fiscal?.commission_rate || 0.10) * 100}%` },
            { label: "IVA", value: `${(fiscal?.iva_rate || 0.21) * 100}%` },
            { label: "Retención IRPF", value: `${(fiscal?.irpf_rate || 0.19) * 100}%` },
            { label: "Reservas", value: `${a.bookings_count}` }
          ].map((r, i) => (
            <div key={i} className="px-3 py-2 rounded-xl bg-[#231f30] border border-white/5 text-xs">
              <span className="text-gray-500">{r.label}</span>
              <span className="text-white font-semibold ml-2">{r.value}</span>
            </div>
          ))}
        </div>

        {/* Quarterly breakdown table */}
        <div className="bg-[#231f30] rounded-2xl border border-white/5 overflow-hidden" data-testid="fiscal-section">
          <div className="flex items-center gap-3 p-5 border-b border-white/5">
            <Receipt className="w-5 h-5 text-violet-400" />
            <h2 className="text-lg font-semibold text-white">Desglose Trimestral</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fiscal-table">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-gray-500 font-normal py-3 px-5">Trimestre</th>
                  <th className="text-right text-gray-500 font-normal py-3 px-3">Reservas</th>
                  <th className="text-right text-gray-500 font-normal py-3 px-3">Bruto</th>
                  <th className="text-right text-gray-500 font-normal py-3 px-3">Comisión</th>
                  <th className="text-right text-gray-500 font-normal py-3 px-3">Base Imp.</th>
                  <th className="text-right text-gray-500 font-normal py-3 px-3">IVA</th>
                  <th className="text-right text-gray-500 font-normal py-3 px-3">IRPF</th>
                  <th className="text-right text-gray-500 font-normal py-3 px-5">Neto</th>
                </tr>
              </thead>
              <tbody>
                {fiscal?.quarters?.map((q) => (
                  <tr key={q.quarter} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="py-4 px-5">
                      <span className="text-white font-medium">{q.label}</span>
                      <span className="text-gray-600 text-xs ml-2">{q.months}</span>
                    </td>
                    <td className="text-right text-gray-400 py-4 px-3">{q.bookings_count}</td>
                    <td className="text-right text-white py-4 px-3">{q.gross.toFixed(2)} €</td>
                    <td className="text-right text-gray-500 py-4 px-3">-{q.commission.toFixed(2)} €</td>
                    <td className="text-right text-white py-4 px-3">{q.base.toFixed(2)} €</td>
                    <td className="text-right text-emerald-400 py-4 px-3">+{q.iva.toFixed(2)} €</td>
                    <td className="text-right text-red-400 py-4 px-3">-{q.irpf.toFixed(2)} €</td>
                    <td className="text-right text-violet-400 font-medium py-4 px-5">{q.net.toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-violet-500/5">
                  <td className="py-4 px-5">
                    <span className="text-white font-bold">Total {fiscalYear}</span>
                  </td>
                  <td className="text-right text-white font-medium py-4 px-3">{a.bookings_count}</td>
                  <td className="text-right text-white font-bold py-4 px-3">{a.gross.toFixed(2)} €</td>
                  <td className="text-right text-gray-400 font-medium py-4 px-3">-{a.commission.toFixed(2)} €</td>
                  <td className="text-right text-white font-bold py-4 px-3">{a.base.toFixed(2)} €</td>
                  <td className="text-right text-emerald-400 font-medium py-4 px-3">+{a.iva.toFixed(2)} €</td>
                  <td className="text-right text-red-400 font-medium py-4 px-3">-{a.irpf.toFixed(2)} €</td>
                  <td className="text-right text-violet-400 font-bold py-4 px-5">{a.net.toFixed(2)} €</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-gray-600 text-xs mt-4 leading-relaxed px-1">
          * Datos orientativos. Los importes de IVA e IRPF son estimaciones. Consulta con tu asesor fiscal para la declaración definitiva ante la AEAT.
        </p>
      </div>
    </div>
  );
}
